import { useNavigate, useParams } from '@tanstack/react-router';
import { Formik } from 'formik';

import { UpdateOrderRequest } from '../../common/api/clients/order.api';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { createOverlay } from '../../common/Overlay/Overlay';
import { useOrder } from '../hooks/useOrder';
import { useOrderEdit } from '../hooks/useOrderEdit';
import { toUpdateOrderRequest } from '../model/order.mapper';

import { useValidateOrderEditForm } from './hooks/useValidateOrderEditForm';
import { FormikEditOrderValues, OrderEditForm } from './OrderEditForm';

export const OrderEditPage = () => {
    const { id } = useParams({ from: '/_app/order_/edit/$id' });
    const navigate = useNavigate({ from: '/order/edit/$id' });

    const { data: order, isPending, isError } = useOrder(id);
    const orderEditMutation = useOrderEdit();
    const formikValidate = useValidateOrderEditForm();

    if (isPending) {
        return <LoadingIndicator variant="page" />;
    }

    if (isError) {
        return <AdvancedError />;
    }

    const initialValues: FormikEditOrderValues = {
        id: order.id,
        vesselId: order.vesselId,
        customerId: order.customerId,
        portId: order.portId,
        dateDelivery: order.dateDelivery || null,
        buyerReference: order.buyerReference || '',
        agentId: order.agentId || '',
        items: order.items,
        samplekits: order.samplekits,
        receiverSupplierIds: order.receiverSupplierIds || [],
        spot: order.spot || false,
        state: order.state,
    };

    const onSubmit = async (values: FormikEditOrderValues) => {
        const updateOrderRequest: UpdateOrderRequest = {
            // Propagate existing values from the current order
            ...toUpdateOrderRequest(order),
            // Update editable values
            vesselId: values.vesselId,
            portId: values.portId,
            dateDelivery: values.dateDelivery || null,
            buyerReference: values.buyerReference !== '' ? values.buyerReference : null,
            agentId: values.agentId !== '' ? values.agentId : null,
            items: values.items,
            samplekits: values.samplekits,
            state: values.state || null,
        };

        await orderEditMutation.mutateAsync({
            orderId: values.id,
            order: updateOrderRequest,
        });

        navigate({ to: '/order/$id', params: { id: values.id } });
    };

    return createOverlay(
        <Formik<FormikEditOrderValues>
            initialValues={initialValues}
            component={OrderEditForm}
            validateOnMount
            validateOnChange={false}
            validate={formikValidate}
            onSubmit={onSubmit}
        />
    );
};
